import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Icon } from '../common'
import './PromptPreview.css'

/**
 * PromptPreview - Displays the generated prompt with copy functionality
 * 
 * Props:
 * - prompt: The main generated prompt string
 * - negativePrompt: The negative prompt string (optional)
 * - onCopy: Callback when copied
 */
export default function PromptPreview({ prompt, negativePrompt }) {
  const [copied, setCopied] = useState(null) // 'prompt' | 'negative' | 'both' | null

  const handleCopy = async (type) => {
    let textToCopy = ''
    
    switch (type) {
      case 'prompt':
        textToCopy = prompt
        break
      case 'negative':
        textToCopy = negativePrompt
        break
      case 'both':
        textToCopy = `${prompt}${negativePrompt ? `\n\n--Negative Prompt--\n${negativePrompt}` : ''}`
        break
      default:
        return
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const isEmpty = !prompt && !negativePrompt

  return (
    <div className="prompt-preview">
      <div className="prompt-preview__header">
        <div className="prompt-preview__title-group">
          <span className="prompt-preview__icon"><Icon name="sparkle" size={20} /></span>
          <h3 className="prompt-preview__title">Generated Prompt</h3>
        </div>
        
        <div className="prompt-preview__actions">
          <button
            className="prompt-preview__copy-btn"
            onClick={() => handleCopy('both')}
            disabled={isEmpty}
            title="Copy all to clipboard"
          >
            <AnimatePresence mode="wait">
              {copied === 'both' ? (
                <motion.span
                  key="check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  <Icon name="check" size={14} />
                </motion.span>
              ) : (
                <motion.span
                  key="copy"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  <Icon name="copy" size={14} />
                </motion.span>
              )}
            </AnimatePresence>
            <span>Copy All</span>
          </button>
        </div>
      </div>
      
      {isEmpty ? (
        <div className="prompt-preview__empty">
          <span className="prompt-preview__empty-icon"><Icon name="prompts" size={32} /></span>
          <p>Enable sections and fill in fields to generate your prompt</p>
        </div>
      ) : (
        <div className="prompt-preview__content">
          {/* Main Prompt */}
          {prompt && (
            <div className="prompt-preview__section">
              <div className="prompt-preview__section-header">
                <span className="prompt-preview__section-label">Prompt</span>
                <button
                  className="prompt-preview__section-copy"
                  onClick={() => handleCopy('prompt')}
                  title="Copy prompt"
                >
                  {copied === 'prompt' ? <Icon name="check" size={14} /> : <Icon name="copy" size={14} />}
                </button>
              </div>
              <div className="prompt-preview__text prompt-preview__text--main">
                {prompt}
              </div>
              <div className="prompt-preview__char-count">
                {prompt.length} characters
              </div>
            </div>
          )}
          
          {/* Negative Prompt */}
          {negativePrompt && (
            <div className="prompt-preview__section prompt-preview__section--negative">
              <div className="prompt-preview__section-header">
                <span className="prompt-preview__section-label">Negative Prompt</span>
                <button
                  className="prompt-preview__section-copy"
                  onClick={() => handleCopy('negative')}
                  title="Copy negative prompt"
                >
                  {copied === 'negative' ? <Icon name="check" size={14} /> : <Icon name="copy" size={14} />}
                </button>
              </div>
              <div className="prompt-preview__text prompt-preview__text--negative">
                {negativePrompt}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

